import { Node } from "reactflow";

export type GlobalGoogleSpreadsheet = { spreadsheet_id: string; spreadsheet_name?: string };

const SHEET_HINTS: Array<{ pattern: RegExp; sheet: string }> = [
   { pattern: /job.?document/i, sheet: "JobDocuments" },
   { pattern: /find.?candidate|append.?new.?candidate|update.?existing.?candidate|candidate/i, sheet: "Candidates" },
   { pattern: /recruitment.?data|spreadsheet-1\b/i, sheet: "Candidates" },
   { pattern: /\bjobs?\b/i, sheet: "Jobs" },
   { pattern: /branch/i, sheet: "Branches" },
   { pattern: /update.?event.?capacity|read.?events|events-1\b|\bevents?\b/i, sheet: "Events" },
   { pattern: /create.?reservation|mark.?reminder|mark.?result|read.?reservations|reservations-1\b|reservation|reminder|result.?notif/i, sheet: "Reservations" },
   { pattern: /waiting.?list|waitlist|waitingList-1\b/i, sheet: "WaitingList" },
   { pattern: /mark.?docs|missing.?doc|read.?documents|documents-1\b|document/i, sheet: "Documents" },
   { pattern: /escalat/i, sheet: "Escalations" },
   { pattern: /unanswered/i, sheet: "UnansweredQuestions" },
   { pattern: /hiring.?plan/i, sheet: "HiringPlans" },
   { pattern: /transfer/i, sheet: "AccountTransfers" },
   { pattern: /stage.?history/i, sheet: "StageHistory" },
];

const isPlaceholder = (value?: string | null) => /^<<.+>>$/.test(String(value || "").trim());

/**
 * Canonical tab this spreadsheet node should use by default.
 * Priority: intendedSheet (template) → name/index hints → current sheet_name.
 * User overrides are preserved separately via sheetNameUserOverride.
 */
export function resolveDefaultSheetName(node: {
   data?: { name?: string; index?: string; value?: any };
}): string {
   const intended = String(node.data?.value?.intendedSheet || "").trim();
   if (intended && !isPlaceholder(intended)) return intended;

   const haystack = `${node.data?.name || ""} ${node.data?.index || ""}`;
   for (const { pattern, sheet } of SHEET_HINTS) {
      if (pattern.test(haystack)) return sheet;
   }

   const existing = String(node.data?.value?.sheet_name || "").trim();
   if (existing && !isPlaceholder(existing)) return existing;
   return "";
}

/** Find the best matching tab name from a live Google Sheets tab list. */
export function matchSheetFromList(
   preferred: string,
   sheetNames: string[]
): string {
   const want = String(preferred || "").trim();
   if (!want || !sheetNames.length) return "";
   const exact = sheetNames.find((n) => n === want);
   if (exact) return exact;
   const ci = sheetNames.find((n) => n.toLowerCase() === want.toLowerCase());
   if (ci) return ci;
   const loose = sheetNames.find(
      (n) =>
         n.toLowerCase().includes(want.toLowerCase()) ||
         want.toLowerCase().includes(n.toLowerCase())
   );
   return loose || "";
}

export function hasRealLocalDocumentOverride(
   value: { spreadsheet_id?: string; isGlobalSpreadsheetId?: boolean } | null | undefined,
   globalSpreadsheet?: GlobalGoogleSpreadsheet | null
): boolean {
   if (value?.isGlobalSpreadsheetId !== false) return false;
   const sid = (value?.spreadsheet_id || "").trim();
   const globalId = (globalSpreadsheet?.spreadsheet_id || "").trim();
   return !!sid && !isPlaceholder(sid) && !!globalId && sid !== globalId;
}

export function nodeUsesGlobalSpreadsheet(
   value:
      | {
           spreadsheet_id?: string;
           isGlobalSpreadsheetId?: boolean;
           globalSpreadsheetName?: string;
        }
      | null
      | undefined,
   globalSpreadsheet?: GlobalGoogleSpreadsheet | null
): boolean {
   if (value?.isGlobalSpreadsheetId === false) return false;
   if (value?.isGlobalSpreadsheetId === true) return true;
   if (globalSpreadsheet?.spreadsheet_id) return true;
   const sid = (value?.spreadsheet_id || "").trim();
   if (sid && globalSpreadsheet?.spreadsheet_id && sid === globalSpreadsheet.spreadsheet_id) {
      return true;
   }
   return false;
}

export function getEffectiveGlobalSpreadsheet(
   value:
      | { spreadsheet_id?: string; globalSpreadsheetName?: string }
      | null
      | undefined,
   globalSpreadsheet?: GlobalGoogleSpreadsheet | null
): GlobalGoogleSpreadsheet | null {
   if (globalSpreadsheet?.spreadsheet_id) return globalSpreadsheet;
   const sid = (value?.spreadsheet_id || "").trim();
   if (!sid || isPlaceholder(sid)) return null;
   return {
      spreadsheet_id: sid,
      spreadsheet_name: value?.globalSpreadsheetName,
   };
}

function buildGlobalFilesCache(spreadsheet: GlobalGoogleSpreadsheet) {
   return [
      {
         id: spreadsheet.spreadsheet_id,
         name: spreadsheet.spreadsheet_name || spreadsheet.spreadsheet_id,
      },
   ];
}

export function patchSpreadsheetNodeValue(
   node: Node<any>,
   spreadsheet: GlobalGoogleSpreadsheet | null
): Node<any> {
   if (node.data?.action !== "spreadsheet") return node;
   if (spreadsheet && hasRealLocalDocumentOverride(node.data?.value, spreadsheet)) return node;

   if (!spreadsheet) {
      if (!node.data?.value?.isGlobalSpreadsheetId) return node;
      return {
         ...node,
         data: {
            ...node.data,
            value: {
               ...node.data.value,
               spreadsheet_id: "",
               isGlobalSpreadsheetId: false,
               globalSpreadsheetName: "",
               files: [],
            },
         },
      };
   }

   const value = node.data?.value || {};
   const keepUserSheet =
      value.sheetNameUserOverride === true &&
      String(value.sheet_name || "").trim() &&
      !isPlaceholder(value.sheet_name);
   const sheet_name = keepUserSheet
      ? String(value.sheet_name).trim()
      : resolveDefaultSheetName(node);

   return {
      ...node,
      data: {
         ...node.data,
         value: {
            ...value,
            spreadsheet_id: spreadsheet.spreadsheet_id,
            isGlobalSpreadsheetId: true,
            globalSpreadsheetName: spreadsheet.spreadsheet_name || "",
            files: buildGlobalFilesCache(spreadsheet),
            ...(sheet_name ? { sheet_name } : {}),
            // Re-applying global document restores template defaults unless user locked a tab.
            ...(keepUserSheet ? {} : { sheetNameUserOverride: false }),
         },
      },
   };
}

export function applySpreadsheetToNodes(
   setNodes: (payload: Node<any>[] | ((nodes: Node<any>[]) => Node<any>[])) => void,
   spreadsheet: GlobalGoogleSpreadsheet | null
): Node<any>[] {
   let patched: Node<any>[] = [];
   setNodes((nodes) => {
      patched = nodes.map((node) => patchSpreadsheetNodeValue(node, spreadsheet));
      return patched;
   });
   return patched;
}

export function applySpreadsheetToNodeList(
   nodes: Node<any>[],
   spreadsheet: GlobalGoogleSpreadsheet | null
): Node<any>[] {
   if (!spreadsheet?.spreadsheet_id) return nodes;
   return nodes.map((node) => patchSpreadsheetNodeValue(node, spreadsheet));
}
